import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Check, Star, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { WaitlistDialog } from "../waitlist-dialog";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";
import { MobileEditorialHeading } from "./mobile-editorial-heading";

export function MobilePricing() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(true);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);

  const plans = [
    { id: "starter", monthly: 79, annual: 63,
      name: t("pricing.plans.starter.name"), desc: t("pricing.plans.starter.desc"),
      features: t("pricing.plans.starter.features", { returnObjects: true }) as string[] },
    { id: "pro", monthly: 199, annual: 159, highlight: true,
      name: t("pricing.plans.pro.name"), desc: t("pricing.plans.pro.desc"),
      features: t("pricing.plans.pro.features", { returnObjects: true }) as string[] },
    { id: "studio", monthly: null as number | null, annual: null as number | null,
      name: t("pricing.plans.studio.name"), desc: t("pricing.plans.studio.desc"),
      features: t("pricing.plans.studio.features", { returnObjects: true }) as string[] },
  ];

  return (
    <section id="pricing" className="mob-surface-0 px-5 py-16 text-white">
      <div className="mb-6 border-t border-white/10 pt-8">
        <MobileEditorialHeading index="05" eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} />
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[11px] font-medium text-amber-200">
          <Clock className="h-3 w-3" /> {t("pricing.notice")}
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 rounded-xl border mob-hairline mob-surface-1 p-1">
        <button onClick={() => setAnnual(false)}
          className={cn("mob-tap rounded-lg px-3 py-2 text-[13px] font-medium", !annual ? "bg-white text-black" : "text-white/70")}>
          {t("pricing.monthly")}
        </button>
        <button onClick={() => setAnnual(true)}
          className={cn("mob-tap rounded-lg px-3 py-2 text-[13px] font-medium inline-flex items-center justify-center gap-1.5", annual ? "bg-white text-black" : "text-white/70")}>
          {t("pricing.annual")} <span className="rounded-full bg-emerald-500/90 px-1.5 py-0.5 text-[9px] font-bold text-black">−20%</span>
        </button>
      </div>

      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.id}
            className={cn(
              "relative rounded-2xl border mob-hairline mob-surface-1 p-5",
              p.highlight && "ring-1 ring-white/40",
            )}>
            {p.highlight && (
              <div className="absolute -top-2.5 left-5 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black">
                <Star className="h-2.5 w-2.5" /> {t("pricing.mostChosen")}
              </div>
            )}
            <h3 className="mob-h3">{p.name}</h3>
            <p className="mob-body mt-1 mob-text-dim">{p.desc}</p>
            <div className="mt-4 flex items-baseline gap-1">
              {p.monthly === null ? (
                <span className="text-[28px] font-semibold tracking-tight">{t("pricing.onRequest")}</span>
              ) : (
                <>
                  <span className="text-[40px] font-semibold tracking-tight">€{annual ? p.annual : p.monthly}</span>
                  <span className="text-[13px] text-white/55">{t("pricing.perMonth")}</span>
                </>
              )}
            </div>
            {annual && p.monthly !== null && (
              <p className="mt-1 text-[11px] text-emerald-300">
                {t("pricing.save", { amount: (p.monthly! - (p.annual as number)) * 12 })}
              </p>
            )}
            <ul className="mt-4 space-y-2.5 text-[13px]">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />
                  <span className="text-white/85">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => { setSelectedPlan(p.id); openWhatsAppContact(`Ciao Gianluca, sono interessato al piano ${p.name} (${annual ? "annuale" : "mensile"}) di LexAI Pro.`); }}
              className={cn(
                "mob-tap mt-5 inline-flex h-11 w-full items-center justify-center rounded-full text-[14px] font-semibold",
                p.highlight ? "bg-white text-black" : "border mob-hairline-strong text-white",
              )}>
              {t("pricing.notify")}
            </button>
            <Link to="/signup" className="mob-tap mt-2 block text-center text-[11px] text-white/55">
              {t("pricing.orFree")}
            </Link>
          </div>
        ))}
      </div>

      <p className="mt-6 text-center text-[12px] text-white/55">{t("pricing.footer")}</p>
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} plan={selectedPlan} source="pricing" />
    </section>
  );
}
